import React, { useState, useRef, useEffect } from 'react';
import '../css/businessContact.css';
import '../css/businessContactResponsive.css';
import SwitchContactCard from '../../../../component-module/component-services/card/switch-contact/ts/switchContactCard';
import TextField from '../../../../component-module/component-services/input/text-field/ts/textField';
import SelectField from '../../../../component-module/component-services/dropdown/select-field/ts/selectField';
import TextArea from '../../../../component-module/component-services/input/textarea/ts/textArea';
import Checkbox from '../../../../component-module/component-services/checkbox/ts/checkbox';
import { CountryOption, CountrySelect } from '../../../../component-module/component-services/input/phone-number';
import RoundButton from '../../../../component-module/component-services/button/round-button/ts/roundButton';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput } from '../../../../context-module/context-configuration/permission-provider/validation';
import { useToast } from '../../../../context-module/context-configuration/toast-provider/bottomToast/ts/ToastProvider';
import { validateBusinessField } from './businessContactValidation';
import { BusinessContactPageData, getCountry, initialBusinessContactFormData, inquiryOptions, mapCountryOptions } from './businessContactData';
import { useCmsPage } from '../../../../context-module/context-configuration/auth-provider/appShell/appShellContent/appUseCntData';
import { API_ENDPOINTS } from '../../../../context-module/context-configuration/auth-provider/apiGroup/apiProvider';
import ApiPost from '../../../../api-module/api-configuration/post-api/postApi';
import { useLoadingAni } from '../../../../context-module/context-configuration/auth-provider/loadinAni/LoadingAniProvider';

export interface CMSContactItem {
  id: number;
  title: string;
  label: string;
}

const contactRouteMap: Record<number, string> = {
  1: '/contact/sales-contact',
  2: '/career',
};

export const mapContactCardsFromCMS = (
  lists: CMSContactItem[],
  navigate: (path: string) => void
) => {
  return lists
    .filter((item) => contactRouteMap[item.id])
    .map((item) => ({
      id: String(item.id),
      description: item.title,
      buttonText: item.label,
      onButtonClick: () => navigate(contactRouteMap[item.id]),
    }));
};

const BusinessContact = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [formData, setFormData] = useState(initialBusinessContactFormData);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { playLoading } = useLoadingAni();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data } = useCmsPage<BusinessContactPageData>(API_ENDPOINTS.ROUTE_URL.BUSINESS_CONTACT);
  const bussinessContactData = data?.businessContact?.cta?.[0].bussinessContactSection?.content[0];
  const contactCards = mapContactCardsFromCMS(bussinessContactData?.headerSection?.lists ?? [], navigate);
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);


  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await getCountry();
        /* istanbul ignore next */
        const list = res?.data?.data ?? [];
        setCountryOptions(mapCountryOptions(list));
      } catch {
        /* istanbul ignore next */
        setCountryOptions([]);
      }
    };

    fetchCountries();
  }, []);

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size (5MB max)
      const allowedTypes = ['application/pdf'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        showToast({
          status: 'error',
          title: 'Invalid Uploaded!',
          description: 'Please upload a PDF file only.',
        });
        return;
      }

      if (file.size > maxSize) {
        showToast({
          status: 'error',
          title: 'Invalid Uploaded!',
          description: 'File size must be less than 5MB.',
        });

        return;
      }

      setSelectedFile(file);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const sanitizedValue = sanitizeInput(value);

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? /* istanbul ignore next */  (e.target as HTMLInputElement).checked : sanitizedValue
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user selects
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Only validate format if field has value - required check is done on submit
    /* istanbul ignore next */
    if (value) {
      const error = validateBusinessField(name, value);
      if (error) {
        setFormErrors(prev => ({ ...prev, [name]: error }));
      } else /* istanbul ignore next */ if (formErrors[name]) {
        // Clear error if value is now valid
        /* istanbul ignore next */
        setFormErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };

  const validateForm = (): { isValid: boolean; hasRequiredErrors: boolean; hasFormatErrors: boolean } => { //NOSONAR
    const errors: Record<string, string> = {};
    let hasRequiredErrors = false;
    let hasFormatErrors = false;

    // Required field validations
    if (!formData.businessName) { //NOSONAR
      errors.businessName = 'required';
      hasRequiredErrors = true;
    } else {
      const nameError = validateBusinessField('businessName', formData.businessName);
      if (nameError) {
        errors.businessName = nameError;
        hasFormatErrors = true;
      }
    }

    if (!formData.businessEmail) { //NOSONAR
      errors.businessEmail = 'required';
      hasRequiredErrors = true;
    } else {
      const emailError = validateBusinessField('businessEmail', formData.businessEmail);
      if (emailError) {
        errors.businessEmail = emailError;
        hasFormatErrors = true;
      }
    }

    if (!formData.inquiresType) {
      errors.inquiresType = 'required';
      hasRequiredErrors = true;
    }

    if (!formData.country) {
      errors.country = 'required';
      hasRequiredErrors = true;
    }

    // Phone validation (required)
    if (!formData.phone) { //NOSONAR
      errors.phone = 'required';
      hasRequiredErrors = true;
    } else {
      const phoneError = validateBusinessField('phone', formData.phone);
      if (phoneError) {
        errors.phone = phoneError;
        hasFormatErrors = true;
      }
    }

    if (formData.message) {
      const messageError = validateBusinessField('message', formData.message);
      if (messageError) {
        errors.message = messageError;
        hasFormatErrors = true;
      }
    }

    /* istanbul ignore next */
    if (!formData.consent) {
      /* istanbul ignore next */
      errors.consent = 'required';
      /* istanbul ignore next */
      hasRequiredErrors = true;
    }

    setFormErrors(errors);
    return {
      isValid: Object.keys(errors).length === 0,
      hasRequiredErrors,
      hasFormatErrors
    };
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    /* istanbul ignore next */
    if (isSubmitting) {
      return;
    }

    const validation = validateForm();

    if (!validation.isValid) {
      /* istanbul ignore next */
      if (validation.hasRequiredErrors) {
        showToast({
          status: "warning",
          title: "Incomplete Form!",
          description: "Please complete all required fields before submitting.",
        });
      }

      if (validation.hasFormatErrors) {
        showToast({
          status: "error",
          title: "Invalid Format!",
          description: "Please check and correct the highlighted fields.",
        });
      }

      return;
    }

    setIsSubmitting(true);
    playLoading();

    try {
      await wait(1000); // delay submit a bit so dimming starts first

      const fd = new FormData();
      fd.append("categoryCode", "business");
      fd.append("subject", formData.inquiresType);
      fd.append("fullName", formData.businessName);
      fd.append("email", formData.businessEmail);
      fd.append("country", formData.country);
      fd.append("telephoneCode", formData.dialCode);
      fd.append("phone", formData.phone);
      fd.append("message", formData.message ?? /* istanbul ignore next */  "");

      if (selectedFile) {
        fd.append("files", selectedFile);
      }

      const response = await ApiPost(API_ENDPOINTS.APPLY_CONTACT, fd);
      console.log(response)
      showToast({
        status: "success",
        title: "Submit Successfully!",
        description: "Your inquiry has been received. We will get back to you soon.",
      });

      if (response?.status === 200) {
        setTimeout(() => {
          navigate("/");
          setFormData(initialBusinessContactFormData);
          setFormErrors({});
          setSelectedFile(null);
        }, 700);
      }
    } catch (err: any) {
      showToast({
        status: "error",
        title: "Submitted Unsuccessfully!",
        description: err?.response?.data?.message ?? "Please try again!",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  console.log(bussinessContactData)

  return (
    <div className='"scl--web-page-business-contact'>
      <div className="scl--web-page-business-contact-container">
        {bussinessContactData?.breadCrumbSection?.visible &&
          <div className="scl--web-page-business-contact-breadcrumb">
            <span //NOSONAR
              className="scl--web-page-business-contact-breadcrumb-text"
              onClick={() => navigate('/contact')}
              onKeyDown={(e) => e.key === 'Enter' && navigate('/contact')}
              role="link"
              tabIndex={0}
            >{bussinessContactData?.breadCrumbSection?.breadCrumbTitleSt}</span>
            <span className="scl--web-page-business-contact-breadcrumb-separator">/</span>
            <span className="scl--web-page-business-contact-breadcrumb-current">{bussinessContactData?.breadCrumbSection?.breadCrumbTitleNd}</span>
          </div>
        }


        <div className="scl--web-page-business-contact-contact-section">

          <div className="scl--web-page-business-contact-header">
            <h1 className="scl--web-page-business-contact-title">{bussinessContactData?.headerSection?.title}</h1>
            <p className="scl--web-page-business-contact-subtitle">
              {bussinessContactData?.headerSection?.description}
            </p>
          </div>

          <div className="scl--web-page-business-contact-content">
            <div className="scl--web-page-business-contact-left-section">
              <SwitchContactCard items={contactCards} />
            </div>

            <div className="scl--web-page-business-contact-right-section">
              <form onSubmit={handleSubmit} className="scl--web-page-business-contact-form">
                <TextField
                  type="text"
                  name="businessName"
                  placeholder="Business name *"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  warning={formErrors.businessName === 'required'}
                  error={!!formErrors.businessName && formErrors.businessName !== 'required'}
                />

                <TextField
                  type="email"
                  name="businessEmail"
                  placeholder="Business e-mail *"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  warning={formErrors.businessEmail === 'required'}
                  error={!!formErrors.businessEmail && formErrors.businessEmail !== 'required'}
                />

                <SelectField
                  name="Subject"
                  placeholder="Subject *"
                  options={inquiryOptions}
                  value={formData.inquiresType}
                  onChange={(value) => handleSelectChange('inquiresType', value)}
                  searchable={true}
                  required
                  warning={formErrors.inquiresType === 'required'}
                  error={!!formErrors.inquiresType && formErrors.inquiresType !== 'required'}
                />

                <div className="scl--web-page-business-contact-form-row">
                  <div className="scl--web-page-business-contact-form-group scl--web-page-business-contact-country-group">
                    <CountrySelect
                      name="country"
                      placeholder="Country *"
                      options={countryOptions}
                      value={formData.country}
                      onChange={(country: CountryOption) => {
                        setFormData(prev => ({
                          ...prev,
                          country: country.code,
                          dialCode: country.dialCode
                        }));

                        if (formErrors.country) {
                          setFormErrors(prev => ({ ...prev, country: '' }));
                        }
                      }}
                      required
                      warning={formErrors.country === 'required'}
                      error={!!formErrors.country && formErrors.country !== 'required'}
                    />
                  </div>

                  <div className="scl--web-page-business-contact-form-group scl--web-page-business-contact-phone-group">
                    <TextField
                      type="tel"
                      name="phone"
                      placeholder="Phone *"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={20}
                      required
                      warning={formErrors.phone === 'required'}
                      error={!!formErrors.phone && formErrors.phone !== 'required'}
                    />
                  </div>
                </div>

                <TextArea
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={150}
                  rows={6}
                  warning={formErrors.message === 'required'}
                  error={!!formErrors.message && formErrors.message !== 'required'}

                />

                <div className="scl--web-page-business-contact-consent-group">
                  <Checkbox
                    name="consent"
                    id="consent"
                    checked={formData.consent}
                    onChange={(checked) => setFormData(prev => ({ ...prev, consent: checked }))}
                  />
                  <span className="scl--web-page-business-contact-consent-label">
                    By clicking the button Agree & send I give my consent to my personal data in accordance with <span className='scl--web-page-business-contact-consent-label-bold'> Privacy Statement </span>for the purpose of potential employment, internship and future career opportunities.

                  </span>
                </div>

                <div className="scl--web-page-business-contact-actions-row">
                  <div className="scl--web-page-business-contact-file-upload-group">
                    <RoundButton
                      text="Attach file"
                      onClick={handleAttachClick}
                      showIcon={false}
                      className="scl--web-page-business-contact-attach-button"
                      type="button"
                    />
                    <span className="scl--web-page-business-contact-file-info">
                      Upload Documents : pdf file only, less than 5MB .
                    </span>
                    {selectedFile && (
                      <span className="scl--web-page-business-contact-file-name">
                        {selectedFile.name}
                      </span>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      autoComplete='off'
                    />
                  </div>

                  <RoundButton
                    text={isSubmitting ? "Submitting..." : "Submit"}
                    onClick={handleSubmit}
                    showIcon={false}
                    className="scl--web-page-business-contact-submit-button"
                    disabled={!formData.consent || isSubmitting}
                    type='submit'
                  />
                </div>
              </form>
            </div>
          </div>

        </div>
      </div>
    </div>

  );
};

export default BusinessContact;
